/*
 * @providesModule PlayerLevelsListContainer
 */

import React, {useCallback, useEffect, useReducer} from 'react';

import _, {debounce} from 'lodash';
import AWSManager from 'AWSManager';
import I18n, {_m} from 'INTL';
import Label from 'Label';
import SkillzModule from 'SkillzModule';
import SKZPlayerLevel from 'SKZPlayerLevel';
import PlayerLevelsApiController from 'PlayerLevelsApiController';
import styles from './PlayerLevelsListContainer.style';

import {Linking, NativeModules, Platform, View} from 'react-native';
import PlayerLevelsList, {DataType, NUM_COLUMNS} from 'PlayerLevelsList';

const ReactUserManager = NativeModules.SKZReactUserManager;

const ADD_NEW_GAME_INDEX = NUM_COLUMNS - 1;

const ADD_GAME_URL = 'https://games.skillz.com/?creative_id=level_new_game_';
const API_CALL_DEBOUNCE_TIME = 3000;

interface IPlayerLevelsListContainerProps {
  /** The user id for which we are fetching the list of player levels */
  userId: number | string;
  /** The initial expanded cell to show (non by default). Kept the original numbering starting at 1 for index 0. */
  expandedCellOnOpen?: number;
}

// eslint-disable-next-line no-shadow
enum EActionType {
  LOAD_FIRST_PAGE,
  LOAD_NEXT_PAGE,
  LOAD_SUCCESS,
  LOAD_ERROR,
}

type Action = {
  type: EActionType;
  payload?: {
    levels: SKZPlayerLevel[];
  };
};

const getAddGameItemProps = (): DataType => {
  const renderAddGameImage = (height: number, width: number) => {
    const labelSize = height * 0.8; // eslint-disable-line no-magic-numbers
    return (
      <View style={[{height, width}, styles.addGameIconBackground]}>
        <Label
          style={[
            styles.addGameIconPlus,
            {width: labelSize, height: labelSize, fontSize: labelSize - 2},
          ]}
          adjustsFontSizeToFit={false}
        >
          {'+'}
        </Label>
      </View>
    );
  };

  const handleAddGamePress = () => {
    AWSManager.trackEvent('PLAYER_LEVELS_ADD_NEW_GAME');
    const addGameUrl = ADD_GAME_URL + SkillzModule.App.GameId;
    Linking.canOpenURL(addGameUrl)
      .then((supported: boolean) => {
        console.log('supported', supported);
        if (supported) {
          if (Platform.OS !== 'web') {
            ReactUserManager.trackEvent(ReactUserManager.Event.PlayerLevelsAddNewGame, '');
          }
          return Linking.openURL(addGameUrl);
        }
      })
      .catch((err: String): void => console.error('An error occurred', err));
  };

  return {
    cell: {
      title: _m('Add a New Game'),
      centerImage: renderAddGameImage,
      onPress: handleAddGamePress,
    },
  };
};

//exporting for test purpose
export const getLevelItemProps = (level: SKZPlayerLevel): DataType => {
  const title = level.game_id ? level.game_name : _m('Skillz Level');
  const currentExp = level.getCurrentXp() === 0 ? 0 : I18n.abbreviatedNumber(level.getCurrentXp());
  const xpToNextLevel = I18n.abbreviatedNumber(level.getTotalExperienceToNextLevel());

  let progressText = currentExp + '/' + xpToNextLevel;

  if (level instanceof SKZPlayerLevel && level.isMaxLevel()) {
    progressText = _m('MAX LVL');
  } else if (level.getCurrentXp() > level.getTotalExperienceToNextLevel()) {
    progressText = xpToNextLevel + '/' + xpToNextLevel;
  }

  const achieved_level_experience = level.experience - level.experience_of_previous_level;
  const total_level_experience =
    level.experience_to_next_level - level.experience_of_previous_level;

  // Overriding background color and border of Skillz Level to support updated branding
  const backgroundColorInnerCircle = level.isSkillzLevel() ? '#FFFFFF' : undefined;
  const innerCircleBorderColor = level.isSkillzLevel() ? '#6B6B6B' : undefined;

  return {
    cell: {
      animated: level.isCurrentGameLevel() || level.isSkillzLevel(),
      title,
      subtitle: progressText,
      centerImage: level.game_icon_url,
      levels: [{level: level.current_level, percentage: level.getPercentageToNextLevel()}],
      backgroundColorInnerCircle,
      innerCircleBorderColor,
    },
    expansion: {
      level: level.isMaxLevel()
        ? 'MAX'
        : {
            currentLevel: level.current_level,
            achieved: achieved_level_experience < 0 ? 0 : achieved_level_experience,
            total: total_level_experience <= 0 ? 0 : total_level_experience,
          },
      totalExp: level.experience,
    },
  };
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case EActionType.LOAD_FIRST_PAGE:
      //whiping the data, on first page load, back to initState
      return initState();
    case EActionType.LOAD_NEXT_PAGE:
      return {
        ...state,
        loading: true,
        error: undefined,
      };
    case EActionType.LOAD_SUCCESS: {
      const formattedResponse = (action.payload?.levels?.map((level: SKZPlayerLevel) => {
        return getLevelItemProps(level);
      }) ?? []) as DataType[];
      if (state.data.length === 0) {
        //if no info in there yet (ie: first call)
        formattedResponse.splice(ADD_NEW_GAME_INDEX, 0, getAddGameItemProps());
      }
      return {
        ...state,
        data: state.data.concat(formattedResponse),
        loading: false,
      };
    }
    case EActionType.LOAD_ERROR: {
      return {
        ...state,
        loading: false,
        error: 'Something',
      };
    }
    default:
      return state;
  }
};

type State = {
  loading: boolean;
  data: DataType[];
  error?: any;
};

const initState = (): State => {
  return {
    loading: true,
    data: [],
    error: undefined,
  };
};

const PlayerLevelsListContainer = ({
  userId,
  expandedCellOnOpen,
}: IPlayerLevelsListContainerProps) => {
  const [state, dispach] = useReducer(reducer, {}, initState);

  const loadPage = useCallback(
    (isFirstPage: boolean) => {
      const handleResult = (result: Array<Object>) => {
        const levels = SKZPlayerLevel.make(result);
        dispach({type: EActionType.LOAD_SUCCESS, payload: {levels}});
      };
      const handleError = () => {
        dispach({type: EActionType.LOAD_ERROR});
      };

      if (isFirstPage) {
        dispach({type: EActionType.LOAD_FIRST_PAGE});
        PlayerLevelsApiController.getLongList(userId).then(handleResult).catch(handleError);
      } else {
        dispach({type: EActionType.LOAD_NEXT_PAGE});
        PlayerLevelsApiController.getNextPage().then(handleResult).catch(handleError);
      }
    },
    [userId]
  );

  const loadPageDebounce = debounce(loadPage, API_CALL_DEBOUNCE_TIME, {
    leading: true,
    trailing: false,
  });

  useEffect(() => {
    //we want to make sure that every time the userID change we load the page as it was the first time
    loadPageDebounce(true);
  }, [userId]);

  const onLoadMore = () => {
    loadPageDebounce(false);
  };

  return (
    <PlayerLevelsList
      datas={state.data}
      //   isLoading={state.loading}
      //   hasError={!!state.error}
      onLoadMore={onLoadMore}
      expandedCellIndexOnOpen={expandedCellOnOpen && expandedCellOnOpen - 1}
    />
  );
};

export default PlayerLevelsListContainer;
